import authService from "@/services/auth.service";
import axios from "axios";

export const PUBLIC_API_URL = import.meta.env.VITE_PUBLIC_API_URL || 
  (import.meta.env.PROD ? '' : 'http://localhost:4000');

// Debug: Log the API URL being used
console.log("🔍 Frontend API Configuration:");
console.log("🔍 VITE_PUBLIC_API_URL:", import.meta.env.VITE_PUBLIC_API_URL);
console.log("🔍 Final API Base URL:", PUBLIC_API_URL + "/api");

export const api = axios.create({
  baseURL: PUBLIC_API_URL + "/api",
  withCredentials: false, // Don't send cookies automatically
});

api.interceptors.request.use((config) => {
  const token = authService.getToken();
  let activeOrg = localStorage.getItem("active_org");
  
  // Clear oversized localStorage items proactively
  if (activeOrg && activeOrg.length > 1000) {
    console.warn("⚠️ Clearing oversized active_org from localStorage");
    localStorage.removeItem("active_org");
    activeOrg = null;
  }
  
  // Calculate header size before sending
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  // Only add x-organization-id if it exists and is valid
  if (activeOrg) {
    try {
      const orgId = JSON.parse(activeOrg);
      if (orgId && typeof orgId === 'string' && orgId.length < 100) {
        headers["x-organization-id"] = orgId;
      } else {
        console.warn("Invalid active_org format, not sending header");
      }
    } catch (e) {
      // If parsing fails, ignore it
      console.warn("Failed to parse active_org:", e);
      localStorage.removeItem("active_org");
    }
  }
  
  // Check total header size
  const headerSize = JSON.stringify(headers).length;
  if (headerSize > 8000) {
    console.error("⚠️ Request headers too large:", headerSize, "bytes");
    console.error("Headers:", headers);
    // Clear problematic headers
    delete headers["x-organization-id"];
    localStorage.removeItem("active_org");
  }
  
  // Apply headers to config
  Object.assign(config.headers, headers);
  
  // Log request data for PATCH/POST requests to groups endpoint
  if ((config.method === 'patch' || config.method === 'post') && config.url?.includes('/groups')) {
    console.log('📤 API Request - URL:', config.url);
    console.log('📤 API Request - Method:', config.method?.toUpperCase());
    console.log('📤 API Request - Data:', JSON.stringify(config.data, null, 2));
    if (config.data?.contributionDay !== undefined) {
      console.log('✅ contributionDay is in request data:', config.data.contributionDay);
    } else {
      console.log('❌ contributionDay is NOT in request data');
    }
  }
  
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Debug: Log API errors
    console.error("🚨 API Error:", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      baseURL: error.config?.baseURL
    });

    // Handle 431 error - Request Header Fields Too Large
    if (error?.response?.status === 431 || error?.code === 'ERR_FAILED' && error?.message?.includes('431')) {
      console.warn("⚠️ Request headers too large. Clearing potentially problematic data...");
      // Clear potentially oversized localStorage items
      try {
        const activeOrg = localStorage.getItem("active_org");
        if (activeOrg && activeOrg.length > 1000) {
          localStorage.removeItem("active_org");
          console.log("Cleared oversized active_org");
        }
      } catch (e) {
        console.error("Error clearing localStorage:", e);
      }
      // Retry once after clearing
      if (error.config && !error.config._retry431) {
        error.config._retry431 = true;
        return api.request(error.config);
      }
    }

    const originalRequest = error.config;
    if (error?.response?.status === 401 && !originalRequest._retry) {
      console.log("---- refreshing the token -----");
      originalRequest._retry = true;
      try {
        const data = await authService.refresh_token();
        const access_token = data?.access_token;
        if (access_token) {
          api.defaults.headers["Authorization"] = "Bearer " + access_token;
          originalRequest.headers["Authorization"] = "Bearer " + access_token;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        authService.logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);
